import { useEffect } from 'react';
import { Clock, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DraftRestoredBannerProps {
  /** Relative-time string returned by useFormDraft (e.g. "3 minutes ago"). */
  age: string | null;
  /** Called when the user dismisses or after 6 s auto-dismiss. */
  onDismiss: () => void;
  className?: string;
}

/**
 * [T68P1-e] Inline draft-restored notification rendered **inside** the
 * form Sheet, just below SheetHeader.
 *
 * Replaces the floating toast (`useToast`) which collided with the Sheet
 * overlay on mobile — the toast viewport used `w-full` below the `md:`
 * breakpoint, causing it to overlap the form header at `top-16`.
 *
 * This component is contextually correct: the notification appears where
 * the user is already looking (inside the form), and auto-dismisses after
 * 6 seconds.
 *
 * @see docs/ARCHITECTURE.md — Form Defense & Draft System
 */
export function DraftRestoredBanner({ age, onDismiss, className }: DraftRestoredBannerProps) {
  // Auto-dismiss after 6 seconds
  useEffect(() => {
    const timer = setTimeout(onDismiss, 6000);
    return () => clearTimeout(timer);
  }, [onDismiss]);

  return (
    <div
      data-testid="draft-restored-banner"
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-700/60 dark:bg-amber-950/40',
        className
      )}
    >
      <Clock className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-amber-900 dark:text-amber-200 leading-snug text-sm">
          Draft restored
        </p>
        {age && (
          <p className="text-amber-700 dark:text-amber-400 text-xs mt-0.5">
            Saved {age}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss draft restored notification"
        className="shrink-0 rounded p-0.5 text-amber-600 hover:bg-amber-100 hover:text-amber-800 dark:text-amber-400 dark:hover:bg-amber-900/40 dark:hover:text-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-400 min-h-[28px] min-w-[28px] flex items-center justify-center"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
